import React, { useCallback, useEffect, useRef, useState } from 'react';
import DoshiiImage from 'components/Image/Image';
import { useModalContext } from 'common/Context/ModalContext';
import { ModalData } from 'types';
import ErrorInfo from 'icons/alert_info.svg';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { SelectMenu } from 'components/SelectMenu';
import binImage from 'icons/delete.svg';
import Resizer from 'react-image-file-resizer';
import styles from './DetailsImage.module.scss';

const MB_TO_BYTE = 1000000;
const acceptedFileTypes = 'image/png, image/jpeg, image/jpg';

interface Props {
  name: string | undefined;
  src: string | null | undefined;
  handleImageSubmit: (args: [imageFile: File, optimisticURL: string] | null) => void;
}

const DetailsImage: React.FC<Props> = ({ name, src, handleImageSubmit }) => {
  const {
    MAX_IMAGE_SIZE_MB,
    MIN_IMAGE_HEIGHT_PX,
    MIN_IMAGE_WIDTH_PX,
    MAX_IMAGE_HEIGHT_PX,
    MAX_IMAGE_WIDTH_PX,
    RESIZE_MAX_IMAGE_HEIGHT_PX,
    RESIZE_MAX_IMAGE_WIDTH_PX
  } = getEnvironmentVariables();
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const [imageHasFailed, setImageHasFailed] = useState(false);
  const [newImageURL, setNewImageURL] = useState<string | null>();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { openModal } = useModalContext();

  const handleDeletePhoto = (): void => {
    setSelectedFile(null);
    setNewImageURL(null);
    // closeModal takes 300ms timer to close a modal, after which it clears the activeModal content.
    // This timer allows the content to clear before potentially triggering DoorDash warning modal and then immediately clearing it
    setTimeout(() => handleImageSubmit(null), 350);
  };
  const openDeleteConfirmationDialog = (): void => {
    openModal({
      type: 'DELETE_PHOTO',
      callback: handleDeletePhoto
    });
  };
  const handleFileError = useCallback(() => {
    const sizeError: ModalData = {
      type: 'ERROR',
      icon: ErrorInfo,
      body: `Please upload a jpg, jpeg or png file under ${MAX_IMAGE_SIZE_MB}MB
        (min size: ${MIN_IMAGE_WIDTH_PX}x${MIN_IMAGE_HEIGHT_PX}px, max size: ${MAX_IMAGE_WIDTH_PX}x${MAX_IMAGE_HEIGHT_PX}px).`,
      title: `Oops! Looks like that file format isn’t quite right.`,
      buttons: [
        {
          label: 'Cancel',
          level: 'low',
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          callback: () => setSelectedFile(undefined)
        },
        {
          label: 'Try Again',
          level: 'high',
          // eslint-disable-next-line no-alert
          callback: () => fileInput.current && fileInput.current.click()
        }
      ]
    };
    setSelectedFile(undefined);
    openModal(sizeError);
  }, [MAX_IMAGE_HEIGHT_PX, MAX_IMAGE_SIZE_MB, MAX_IMAGE_WIDTH_PX, MIN_IMAGE_HEIGHT_PX, MIN_IMAGE_WIDTH_PX, openModal]);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }
      const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * MB_TO_BYTE;
      let count = 0;
      const resizeDimensions = [
        {
          width: RESIZE_MAX_IMAGE_WIDTH_PX,
          height: RESIZE_MAX_IMAGE_HEIGHT_PX
        },
        {
          width: Math.ceil(RESIZE_MAX_IMAGE_WIDTH_PX - (RESIZE_MAX_IMAGE_WIDTH_PX - MIN_IMAGE_WIDTH_PX) * 0.3),
          height: Math.ceil(RESIZE_MAX_IMAGE_HEIGHT_PX - (RESIZE_MAX_IMAGE_HEIGHT_PX - MIN_IMAGE_HEIGHT_PX) * 0.3)
        },
        {
          width: Math.ceil(RESIZE_MAX_IMAGE_WIDTH_PX - (RESIZE_MAX_IMAGE_WIDTH_PX - MIN_IMAGE_WIDTH_PX) * 0.6),
          height: Math.ceil(RESIZE_MAX_IMAGE_HEIGHT_PX - (RESIZE_MAX_IMAGE_HEIGHT_PX - MIN_IMAGE_HEIGHT_PX) * 0.6)
        }
      ];

      const imageFileResizer = (
        file: File,
        mimeType: string,
        quality: number,
        maxImageWidth: number,
        maxImageHeight: number
      ): Promise<File> => {
        return new Promise((resolve) => {
          Resizer.imageFileResizer(
            file,
            maxImageWidth,
            maxImageHeight,
            mimeType === 'image/png' ? 'png' : 'jpeg',
            quality,
            0,
            (newFile) => {
              const fileTyped = newFile as File;
              resolve(fileTyped);
            },
            'file',
            MIN_IMAGE_WIDTH_PX,
            MIN_IMAGE_HEIGHT_PX
          );
        });
      };
      const resize = async (file: File): Promise<File | undefined> => {
        let finalFile;

        // We want to run the image through processing at least once to avoid a weird issue we are facing with some images
        // which fail to upload through the file uploader.
        const resizedFile = await imageFileResizer(
          file,
          file.type,
          99,
          file.size > MAX_IMAGE_SIZE_BYTES ? resizeDimensions[count].width : MAX_IMAGE_WIDTH_PX,
          file.size > MAX_IMAGE_SIZE_BYTES ? resizeDimensions[count].height : MAX_IMAGE_HEIGHT_PX
        );
        count += 1;

        if (resizedFile.size > MAX_IMAGE_SIZE_BYTES && count <= resizeDimensions.length) {
          finalFile = await resize(resizedFile);
        } else if (resizedFile.size <= MAX_IMAGE_SIZE_BYTES) {
          finalFile = resizedFile;
        } else {
          finalFile = undefined;
        }
        return finalFile;
      };

      resize(file).then((resizedFile) => {
        if (!resizedFile) {
          handleFileError();
          return;
        }

        const img = new Image();
        img.src = window.URL.createObjectURL(resizedFile);

        img.onload = () => {
          const { height, width } = img;

          if (
            height > MAX_IMAGE_HEIGHT_PX ||
            width > MAX_IMAGE_WIDTH_PX ||
            height < MIN_IMAGE_HEIGHT_PX ||
            width < MIN_IMAGE_WIDTH_PX
          ) {
            handleFileError();
            return;
          }

          setNewImageURL(img.src);
          setImageHasFailed(false);
          setSelectedFile(resizedFile);
        };
        img.onerror = () => {
          handleFileError();
        };
      });
    },
    [
      MAX_IMAGE_HEIGHT_PX,
      MAX_IMAGE_SIZE_MB,
      MAX_IMAGE_WIDTH_PX,
      MIN_IMAGE_HEIGHT_PX,
      MIN_IMAGE_WIDTH_PX,
      RESIZE_MAX_IMAGE_HEIGHT_PX,
      RESIZE_MAX_IMAGE_WIDTH_PX,
      handleFileError
    ]
  );

  useEffect(() => {
    if (selectedFile && newImageURL) {
      handleImageSubmit([selectedFile, newImageURL]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newImageURL, selectedFile]);

  useEffect(() => {
    if (newImageURL) {
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    }
  }, [newImageURL]);

  const oldSrcOrNewSrc = (src && selectedFile !== null) || !!selectedFile;
  const imageToDisplay = !imageHasFailed && oldSrcOrNewSrc;
  return (
    <>
      <input
        data-testid="image-upload"
        type="file"
        value={fileInput.current !== null ? fileInput.current.value : ''}
        onChange={handleFileInput}
        ref={fileInput}
        style={{ display: 'none' }}
        accept={acceptedFileTypes}
        multiple={false}
      />

      {imageToDisplay ? (
        <SelectMenu
          options={[
            {
              callback: () => fileInput.current && fileInput.current.click(),
              value: 'edit-photo',
              type: 'button',
              content: <p>Change Photo</p>
            },
            {
              callback: openDeleteConfirmationDialog,
              value: 'delete-photo',
              type: 'button',
              isRedHoverBackground: true,
              content: (
                <span className={styles.deleteOption}>
                  <img src={binImage} alt="delete" className={styles.deleteImage} />
                  <p>Delete Photo</p>
                </span>
              )
            }
          ]}
          align="left"
          downIcon={null}
          lineBeforeLast
          size="small"
          defaultButtonContent={
            <div className={styles.buttonBase}>
              <DoshiiImage
                testId="product-image"
                onError={() => {
                  setImageHasFailed(true);
                }}
                className={styles.detailsImage}
                alt={(selectedFile?.name || name) as string}
                src={newImageURL ?? src}
              />
              <div className={`${styles.overlay} ${styles.successBorder}`} />
            </div>
          }
          buttonStyle={styles.selectMenuButton}
        />
      ) : (
        <button
          type="button"
          className={styles.buttonBase}
          onClick={() => fileInput.current && fileInput.current.click()}
        >
          <DoshiiImage
            testId="product-image"
            className={styles.detailsImage}
            alt={name || 'no-image'}
            src={imageHasFailed ? '/Images/BrokenImage.svg' : '/Images/NoImage.svg'}
          />
          <div className={`${styles.overlay} ${styles.successBorder} ${imageHasFailed && styles.failed}`} />
        </button>
      )}
      <div className={`${styles.successBorder} ${uploadSuccess && styles.successBorderAnimated}`} />
    </>
  );
};

export default DetailsImage;
